import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import SourceApi from "../../api/SourceApi";
import AddDetails from "./AddDetails";
import EditDetails from "./EditDetails";

import Auth from "../../utils/AuthUtils";
import { can } from "../../utils/permissionUtils";

function List() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  /* ================= AUTH / RBAC ================= */
  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  /* ================= FETCH DATA ================= */
  const fetchSources = async () => {
    try {
      setLoading(true);
      const res = await SourceApi.list();

      if (res.code === 200) {
        setSources(res.data);
        setError(null);
      } else {
        setError(res.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    if (!selectedSource) return;

    // ✅ RBAC check
    if (!can(permissions, "delete", "source")) return;

    try {
      const res = await SourceApi.delete({ id: selectedSource._id });

      if (res.code === 200) {
        const modalEl = document.getElementById("delete-source-modal");
        window.bootstrap?.Modal?.getInstance(modalEl)?.hide();

        setSelectedSource(null);
        fetchSources();
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const filteredSource = sources.filter((item) => {
    const search = searchTerm.toLowerCase().trim();

    return item.source?.toLowerCase().includes(search);
  });

  return (
    <div className="content pb-0">
      {/* ================= HEADER (UI from old code) ================= */}
      <div className="d-flex align-items-center justify-content-between gap-2 mb-4 flex-wrap">
        <h4 className="mb-1">
          Source Management
          <span className="badge badge-soft-primary ms-2">
            {filteredSource.length}
          </span>
        </h4>
      </div>

      <div className="gap-2 d-flex align-items-center mb-3 justify-content-between flex-wrap">
        {/* ✅ Active Logic: Check 'write' permission before showing Add Button */}

      <div className="group-search-box position-relative">
  <i className="ti ti-search search-icon"></i>

  <input
    type="text"
    className="form-control search-input"
    placeholder="Search by Source..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
  />
</div>
        {can(permissions, "write", "source") && (
          <button
            type="button"
            className="btn btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#standard-modal-source"
          >
            <i className="ti ti-square-rounded-plus-filled me-1" />
            Add Source
          </button>
        )}
      </div>

      {/* ================= TABLE (UI from old code) ================= */}
      <div className="card border-0 rounded-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive custom-table">
            <table className="table table-nowrap align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.no</th>
                  <th>Lead Type</th>
                  <th>Lead Group</th>
                  <th>Lead Source</th>
                  <th>Registration URL</th>
                  <th>Created on</th>
                  {(can(permissions, "write", "source") ||
                    can(permissions, "delete", "source")) && (
                    <th className="text-center">Action</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="7" className="text-danger text-center py-4">
                      {error}
                    </td>
                  </tr>
                ) : sources.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      No sources found
                    </td>
                  </tr>
                ) : (
                  filteredSource.map((item, index) => (
                    <tr key={item._id}>
                      <td>{index + 1}</td>
                      <td>
                        <span className="badge badge-soft-info">
                          {item.type?.type || "-"}
                        </span>
                      </td>
                      <td>{item.group?.group || "-"}</td>
                      <td className="fw-bold">{item.source}</td>

                      <td>
                        <Link
                          to={`/LeadRegistration/${item.slug || ""}`}
                          target="_blank"
                          className="btn btn-xs btn-outline-primary"
                        >
                          <i className="ti ti-external-link me-1"></i>
                          Open Url
                        </Link>
                      </td>

                      <td>
                        {new Date(item.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>

                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          {can(permissions, "write", "source") && (
                            <button
                              className="btn btn-soft-success btn-sm"
                              data-bs-toggle="modal"
                              data-bs-target="#edit-source-modal"
                              onClick={() => setSelectedSource(item)}
                            >
                              <i className="ti ti-edit" />
                            </button>
                          )}

                          {can(permissions, "delete", "source") && (
                            <button
                              className="btn btn-soft-danger btn-sm"
                              data-bs-toggle="modal"
                              data-bs-target="#delete-source-modal"
                              onClick={() => setSelectedSource(item)}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ================= MODALS ================= */}
      <div id="standard-modal-source" className="modal fade" tabIndex={-1}>
        <AddDetails onAdded={fetchSources} />
      </div>

      <div className="modal fade" id="edit-source-modal" tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content border-0">
            <EditDetails
              sourceData={selectedSource}
              fetchSources={fetchSources}
              clearSelection={() => setSelectedSource(null)}
            />
          </div>
        </div>
      </div>

      {/* ================= DELETE MODAL ================= */}
      <div className="modal fade" id="delete-source-modal" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-body p-4 text-center">
              <span className="avatar avatar-xl badge-soft-danger rounded-circle mb-3">
                <i className="ti ti-trash fs-24"></i>
              </span>

              <h5 className="mb-1">Delete Source?</h5>
              <p className="small text-muted mb-3">
                Are you sure you want to remove this source?
              </p>

              <div className="d-flex justify-content-center gap-2">
                <button
                  type="button"
                  className="btn btn-light w-100"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger w-100"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
